import { useState, useEffect, useCallback, useRef } from 'react';

// Konami code sequence: up up down down left right left right B A Enter
const KONAMI_SEQUENCE = [
  'ArrowUp',
  'ArrowUp',
  'ArrowDown',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'ArrowLeft',
  'ArrowRight',
  'KeyB',
  'KeyA',
  'Enter',
];

// Mobile gesture sequence: swipe directions followed by 3 taps
type GestureType = 'up' | 'down' | 'left' | 'right' | 'tap';
const MOBILE_SEQUENCE: GestureType[] = [
  'up',
  'up',
  'down',
  'down',
  'left',
  'right',
  'left',
  'right',
  'tap',
  'tap',
  'tap',
];

const SWIPE_THRESHOLD = 50; // Minimum distance for a swipe
const SWIPE_TIME_LIMIT = 300; // Max time in ms for a swipe
const SEQUENCE_TIMEOUT = 3000; // Reset sequence after 3 seconds of inactivity

export function useKonamiCode(): boolean {
  const [activated, setActivated] = useState(false);
  const keySequenceRef = useRef<string[]>([]);
  const gestureSequenceRef = useRef<GestureType[]>([]);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetSequences = useCallback(() => {
    keySequenceRef.current = [];
    gestureSequenceRef.current = [];
  }, []);

  const scheduleReset = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(resetSequences, SEQUENCE_TIMEOUT);
  }, [resetSequences]);

  // Desktop keyboard handler
  useEffect(() => {
    if (activated) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      scheduleReset();

      const key = event.code;
      keySequenceRef.current.push(key);

      // Keep only the last N keys where N = sequence length
      if (keySequenceRef.current.length > KONAMI_SEQUENCE.length) {
        keySequenceRef.current.shift();
      }

      // Check if sequence matches
      if (keySequenceRef.current.length === KONAMI_SEQUENCE.length) {
        const matches = keySequenceRef.current.every(
          (k, i) => k === KONAMI_SEQUENCE[i]
        );
        if (matches) {
          setActivated(true);
          resetSequences();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activated, resetSequences, scheduleReset]);

  // Mobile touch/gesture handler
  useEffect(() => {
    if (activated) return;

    const handleTouchStart = (event: TouchEvent) => {
      if (event.touches.length !== 1) return;
      const touch = event.touches[0];
      touchStartRef.current = {
        x: touch.clientX,
        y: touch.clientY,
        time: Date.now(),
      };
    };

    const handleTouchEnd = (event: TouchEvent) => {
      if (!touchStartRef.current) return;
      if (event.changedTouches.length !== 1) return;

      const touch = event.changedTouches[0];
      const deltaX = touch.clientX - touchStartRef.current.x;
      const deltaY = touch.clientY - touchStartRef.current.y;
      const elapsed = Date.now() - touchStartRef.current.time;

      scheduleReset();

      let gesture: GestureType | null = null;

      // Determine if it's a swipe or tap
      const absX = Math.abs(deltaX);
      const absY = Math.abs(deltaY);

      if (elapsed < SWIPE_TIME_LIMIT) {
        if (absX < SWIPE_THRESHOLD && absY < SWIPE_THRESHOLD) {
          // Tap
          gesture = 'tap';
        } else if (absX > absY && absX >= SWIPE_THRESHOLD) {
          // Horizontal swipe
          gesture = deltaX > 0 ? 'right' : 'left';
        } else if (absY > absX && absY >= SWIPE_THRESHOLD) {
          // Vertical swipe
          gesture = deltaY > 0 ? 'down' : 'up';
        }
      }

      if (gesture) {
        gestureSequenceRef.current.push(gesture);

        // Keep only the last N gestures
        if (gestureSequenceRef.current.length > MOBILE_SEQUENCE.length) {
          gestureSequenceRef.current.shift();
        }

        // Check if sequence matches
        if (gestureSequenceRef.current.length === MOBILE_SEQUENCE.length) {
          const matches = gestureSequenceRef.current.every(
            (g, i) => g === MOBILE_SEQUENCE[i]
          );
          if (matches) {
            setActivated(true);
            resetSequences();
          }
        }
      }

      touchStartRef.current = null;
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [activated, resetSequences, scheduleReset]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return activated;
}
